import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "./api";
import type { AdminNotificationRow } from "./types";

const typeIcon: Record<string, React.ComponentProps<typeof Icon>["name"]> = {
  "user.registered": "users",
  "feedback.created": "message",
  "trial.started": "sparkle",
  "premium.granted": "credit-card",
  "privacy.request": "shield"
};

const typeTone: Record<string, string> = {
  "user.registered": "is-blue",
  "feedback.created": "is-amber",
  "trial.started": "is-green",
  "premium.granted": "is-purple",
  "privacy.request": "is-amber"
};

/** Where each notification type lands when the owner taps it. */
const typeRoute: Record<string, string> = {
  "user.registered": "/users",
  "trial.started": "/subscriptions",
  "premium.granted": "/subscriptions",
  "feedback.created": "/feedback",
  "privacy.request": "/privacy"
};

type TFunc = ReturnType<typeof useI18n>["t"];

/** Localizes the KNOWN backend event patterns for fa readers: titles by type,
 *  body patterns (duration/plans) via safe replacements. Unknown strings pass
 *  through untouched — never fake a translation. */
export function localizeNotification(locale: string, item: AdminNotificationRow): { title: string; body: string } {
  if (locale !== "fa-IR") return { title: item.title, body: item.body };
  const faTitles: Record<string, string> = {
    "premium.granted": "اعطای پریمیوم",
    "trial.started": "شروع دوره آزمایشی",
    "user.registered": "کاربر جدید",
    "feedback.created": "بازخورد جدید",
    "privacy.request": "درخواست حریم خصوصی"
  };
  let body = item.body
    .replace(/(\d+)\s*Monate?/g, "$1 ماه")
    .replace(/(\d+)\s*Tag(e?)\b/g, "$1 روز");
  body = body.replace(/\d+/g, (d) => d.replace(/[0-9]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[+digit]));
  return { title: faTitles[item.type] ?? item.title, body };
}

/** German relative time ("vor 5 Min."), minute precision is enough here. */
export function relativeDe(t: TFunc, iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return t("admin.notif.justNow");
  if (minutes < 60) return t("admin.notif.minutesAgo", { minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("admin.notif.hoursAgo", { hours });
  return t("admin.notif.daysAgo", { days: Math.floor(hours / 24) });
}

interface LiveNotifications {
  unread: number;
  items: AdminNotificationRow[];
  loaded: boolean;
  markAllRead: () => Promise<void>;
  markSomeRead: (ids: string[]) => Promise<void>;
}

/**
 * Live owner events for the console bell: the unread badge polls
 * /notifications/count every 30 seconds, the list refreshes with it while the
 * panel is open. Failures keep the last known state (no fake zeros).
 */
export function useAdminNotifications(open: boolean): LiveNotifications {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<AdminNotificationRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const requestId = useRef(0);

  const loadList = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    try {
      const result = await adminApi<AdminNotificationRow[]>("/notifications?limit=50");
      if (requestId.current !== id) return;
      setItems(result);
      setLoaded(true);
    } catch {
      // Stale list stays visible; the next poll retries.
    }
  }, []);

  const loadCount = useCallback(async () => {
    try {
      const result = await adminApi<{ unread: number }>("/notifications/count");
      setUnread(result.unread);
    } catch {
      // Keep the previous badge on transient failures.
    }
  }, []);

  useEffect(() => { void loadCount(); }, [loadCount]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadCount();
      if (open) void loadList();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [open, loadCount, loadList]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      await loadList();
      await loadCount();
    })();
  }, [open, loadList, loadCount]);

  const markAllRead = useCallback(async () => {
    try {
      const result = await adminApi<{ unread: number }>("/notifications/read", { method: "POST", body: JSON.stringify({ ids: [] }) });
      setUnread(result.unread);
      setItems((current) => current.map((item) => ({ ...item, read: true })));
    } catch {
      // Retry happens on the next poll; nothing fake in the meantime.
    }
  }, []);

  // Marks exactly the tapped notifications read (single item from the panel).
  const markSomeRead = useCallback(async (ids: string[]) => {
    try {
      const result = await adminApi<{ unread: number }>("/notifications/read", { method: "POST", body: JSON.stringify({ ids }) });
      setUnread(result.unread);
      setItems((current) => current.map((item) => (ids.includes(item.id) ? { ...item, read: true } : item)));
    } catch {
      // Read state refreshes on the next poll; the jump happens regardless.
    }
  }, []);

  return { unread, items, loaded, markAllRead, markSomeRead };
}

/** 44px bell with unread badge; `children` renders the slide-down panel. */
export function AdminNotificationBell({ unread, open, onToggle, children }: {
  unread: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="admin-bell">
      <button
        type="button"
        className="admin-bell__button"
        aria-label={open ? t("admin.notif.close") : t("admin.notif.open")}
        aria-expanded={open}
        onClick={onToggle}
      >
        <Icon name="bell" size={20} />
        {unread > 0 && (
          <span className="admin-bell__badge" aria-label={t("admin.notif.unreadCount", { count: unread })}>{unread > 99 ? t("admin.notif.unreadMax") : unread}</span>
        )}
      </button>
      {open && children}
    </div>
  );
}

/** Slide-down event feed: icon per type, unread highlighted with a dot.
 *  Every item is a real button — tapping it jumps to the matching section
 *  (Abos, Nutzerkonten, Feedback), marks the entry read and closes the panel,
 *  mirroring the user-app notification behaviour. */
export function AdminNotificationsPanel({ items, loaded, unread, onMarkAllRead, onMarkSomeRead, onAfterNavigate }: {
  items: AdminNotificationRow[];
  loaded: boolean;
  unread: number;
  onMarkAllRead: () => void;
  onMarkSomeRead: (ids: string[]) => void;
  onAfterNavigate: () => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const openItem = (item: AdminNotificationRow) => {
    const route = typeRoute[item.type];
    if (route) navigate(route);
    if (!item.read) onMarkSomeRead([item.id]);
    onAfterNavigate();
  };
  return (
    <div className="admin-notifications" role="region" aria-label={t("admin.notif.panelAriaLabel")}>
      <div className="admin-notifications__head">
        <strong>{t("admin.notif.panelAriaLabel")}</strong>
        <button
          type="button"
          className="admin-notifications__mark"
          disabled={unread === 0}
          onClick={onMarkAllRead}
        >
          {t("admin.notif.markAllRead")}
        </button>
      </div>
      <div className="admin-notifications__list">
        {!loaded && <p className="admin-notifications__empty">{t("admin.notif.loading")}</p>}
        {loaded && items.length === 0 && <p className="admin-notifications__empty">{t("admin.notif.empty")}</p>}
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`admin-notifications__item ${item.read ? "" : "is-unread"}`}
            title={t("admin.notif.openSection")}
            onClick={() => openItem(item)}
          >
            <i className={`admin-notifications__glyph ${typeTone[item.type] ?? "is-purple"}`} aria-hidden="true">
              <Icon name={typeIcon[item.type] ?? "bell"} size={16} />
            </i>
            <div className="admin-notifications__text">
              <strong>
                {localizeNotification(locale, item).title}
                {!item.read && <i className="admin-notifications__dot" aria-label={t("admin.notif.unread")} />}
              </strong>
              <span>{localizeNotification(locale, item).body}</span>
              <small>{relativeDe(t, item.createdAt)}</small>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
